
import Vue from "vue";

import Container from "@/components/Container.vue";
import VibrationParameterTab from "@/features/rvsCalculation/components/VibrationParameterTab.vue";
import ThresholdFmTab from "@/features/rvsCalculation/components/ThresholdFmTab.vue";
import { RvsCalculationJob } from "@/clients/RvsCalculationClient";

const POLL_INTERVAL_MS = 5000;

export default Vue.extend({
  name: "RvsCalculationPage",
  components: { Container, VibrationParameterTab, ThresholdFmTab },
  data() {
    return {
      tab: 0,
      activeJob: null as RvsCalculationJob | null,
      pollTimer: null as any,
      isPolling: false,
      isCreatingJob: false,
    };
  },
  computed: {
    isJobRunning(): boolean {
      return this.activeJob?.status === "running";
    },
    jobBusy(): boolean {
      return this.isJobRunning || this.isCreatingJob;
    },
    progressPct(): number {
      if (!this.activeJob || !this.activeJob.totalFiles) return 0;
      return Math.round((this.activeJob.doneFiles / this.activeJob.totalFiles) * 100);
    },
    bannerClass(): string {
      if (!this.activeJob) return "";
      if (this.isJobRunning) return "job-banner--running";
      return this.activeJob.failedFiles ? "job-banner--done-errors" : "job-banner--done";
    },
  },
  async created() {
    const role = this.$store.getters.user?.role?.toUpperCase();
    if (role !== "ADMIN") {
      this.$router.replace({ name: "CompaniesPage" });
      return;
    }
    this.tab = this.$route.query.tab === "threshold" ? 1 : 0;
    await this.restoreActiveJob();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    onJobCreated(jobId: number) {
      this.pollJob(jobId);
      this.startPolling(jobId);
    },
    async retryFailed() {
      if (!this.activeJob) return;
      const failedIds = this.activeJob.files.filter((file) => file.processStatus === "failed").map((file) => file.id);
      if (!failedIds.length) return;
      try {
        this.isCreatingJob = true;
        const { jobId } = await (this as any).$dep.rvsCalculationService.createJob(failedIds);
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Calculation started — you can leave this page",
          type: "success",
        });
        this.onJobCreated(jobId);
      } catch (error: any) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error?.response?.data?.detail || "error: failed to start calculation job",
          type: "error",
        });
      } finally {
        this.isCreatingJob = false;
      }
    },
    async restoreActiveJob() {
      try {
        const job = await (this as any).$dep.rvsCalculationService.getActiveJob();
        if (!job) return;
        if (job.status === "running") {
          this.activeJob = job;
          this.startPolling(job.id);
          return;
        }
        // A finished job is restored only when it still needs attention: a spectrum
        // job with retryable failures, or a failed threshold recalculation.
        if (job.failedFiles > 0) this.activeJob = job;
      } catch {
        /* no active job */
      }
    },
    async pollJob(jobId: number) {
      if (this.isPolling) return;
      try {
        this.isPolling = true;
        const job = await (this as any).$dep.rvsCalculationService.getJob(jobId);
        this.activeJob = job;
        if (job.status !== "running") {
          this.stopPolling();
          this.$store.dispatch("openAlertMessage", this.terminalToast(job));
        }
      } catch {
        /* keep polling; transient errors are retried on the next tick */
      } finally {
        this.isPolling = false;
      }
    },
    terminalToast(job: RvsCalculationJob) {
      if (job.type === "threshold_fm") {
        return job.failedFiles
          ? {
              open: true,
              message: job.errorMessage || `Threshold recalculation failed for ${job.sensorId}`,
              type: "error",
            }
          : { open: true, message: `Threshold updated for ${job.sensorId}`, type: "success" };
      }
      return {
        open: true,
        message: `Job #${job.id} finished — ${job.doneFiles - job.failedFiles} succeeded, ${job.failedFiles} failed`,
        type: job.failedFiles ? "error" : "success",
      };
    },
    startPolling(jobId: number) {
      this.stopPolling();
      this.pollTimer = setInterval(() => this.pollJob(jobId), POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
  },
  watch: {
    tab(value: number) {
      const name = value === 1 ? "threshold" : "vibration";
      if (this.$route.query.tab !== name) {
        this.$router.replace({ query: { ...this.$route.query, tab: name } }).catch(() => {
          /* ignore redundant navigation */
        });
      }
    },
  },
});
